import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { useForm } from 'react-hook-form'
import { useParams } from 'react-router'
import { useNavigate } from '#/hooks/use-navigate'
import { Button } from '#/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '#/components/ui/dialog'
import { clientInvitationAPI } from '#/api/client-invitations'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage
} from '#/components/ui/form'
import { Input } from '#/components/ui/input'
import { AlertBox } from '#/components/ui/alert-box'
import { mutationError } from '#/lib/mutation-error'
import { toast } from 'sonner'


interface FormInputs {
  email: string
}

export default function InviteMemberModal() {
  const { t } = useTranslation([ 'customers', 'general' ])
  const params = useParams() as { organization: string; customer: string }
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const close = () => navigate('..')

  const form = useForm<FormInputs>({
    defaultValues: {
      email: ''
    }
  })

  const mutation = useMutation({
    mutationKey: [ 'organizations', params.organization, 'customers', params.customer, 'members', 'invite' ],
    mutationFn: (inputs: FormInputs) => clientInvitationAPI.create(params.customer, inputs),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [ 'organizations', params.organization, 'customers', params.customer, 'members' ] })
      toast.success(t('invite_member.success'))
      navigate('..')
    }
  })

  return (
    <Dialog open modal onOpenChange={close}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('invite_member.title')}</DialogTitle>
          <DialogDescription>{t('invite_member.description')}</DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(inputs => mutation.mutate(inputs))}
            className="flex flex-col gap-4"
          >

            {mutation.error && (
              <AlertBox
                error
                title={mutationError(mutation.error, {
                  default: t('invite_member.errors.default'),
                  forbidden: t('invite_member.errors.forbidden')
                })}
              />
            )}

            <FormField
              name="email"
              control={form.control}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('invite_member.email')}</FormLabel>
                  <FormControl>
                    <Input
                      type="email"
                      required
                      autoFocus
                      autoComplete="email"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter className="gap-2 justify-start">
              <Button type="button" variant="outline" onClick={close}>
                {t('general:cancel')}
              </Button>
              <Button type="submit" disabled={mutation.isPending}>
                {t('invite_member.submit')}
              </Button>
            </DialogFooter>

          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
